import { useNavigate, useParams } from "react-router-dom";
import { styled, Tooltip, useMediaQuery } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { GetAccessoryById } from "../../api/services/products.api";
import { getBaseUrl } from "../../api/baseUrl";
import { isMobile } from "react-device-detect";
import { NavigateBefore } from "@mui/icons-material";
import { BrandEnumArray } from "../../util/appUtil";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Box from "@mui/material/Box";
import ShareIcon from "@mui/icons-material/Share";
import Skeleton from "@mui/material/Skeleton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ProductItemPageFooter from "../Mobiles/MobileMode/ProductItemPageFooter";
import MobileReadyToBuy from "../Mobiles/MobileReadyToBuy/MobileReadyToBuy";

const Root = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "space-between",
  borderBox: "box-sizing",
  gap: "50px",
}));

const Content = styled("div")(({ theme }) => ({
  width: "100%",
}));

const NavigateContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "5px",
}));

const NavigateItem = styled("div")(({ theme }) => ({
  fontWeight: 600,
  cursor: "pointer",
  fontSize: "14px",
  display: "flex",
  alignItems: "center",
}));

const Divider = styled("div")(({ theme }) => ({
  width: "100%",
  height: "2px",
  background: "#f2f2f2",
}));

const ProductMainInfoItem = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minWidth: "0px",
  boxSizing: "border-box",
}));

const ProductMainInfoItemKey = styled("div")(({ theme }) => ({
  fontSize: "12px",
  lineHeight: "20px",
  color: "rgba(0,0,0,.6)",
}));

const ProductMainInfoItemValue = styled("div")(({ theme }) => ({
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: "20px",
  color: "#000",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "auto",
  maxWidth: isMobile ? "200px" : "150px",
}));

const ByItemContainer = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  background: "#E5F5FF",
  borderRadius: "12px",
  flexDirection: "column",
  gap: "20px",
}));

const ByItemContainerTitle = styled("div")(({ theme }) => ({
  padding: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "2px solid #fff",
  width: "100%",
  height: "50px",
  minWidth: "0px",
  boxSizing: "border-box",
}));

const Price = styled("div")(() => ({
  fontWeight: 500,
  fontSize: "20px",
  lineHeight: "24px",
}));

const BuyButton = styled("div")(() => ({
  padding: "10px",
  display: "flex",
  alignItems: "center",
  boxSizing: "border-box",
  minWidth: "0px",
  background: "#1875CC",
  userSelect: "none",
  cursor: "pointer",
  borderRadius: "12px",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 400,
  width: "100%",
  gap: "10px",
  transition: "background 0.3s ease-in-out, transform 0.3s ease-in-out",

  "&:hover": {
    background: "#0665bf",
    transform: "scale(1.023)",
  },
}));

const StyledImageContainer = styled("div")(({ isSelected }) => ({
  borderRadius: "8px",
  border: isSelected ? "1px solid #1875CC" : "1px solid transparent",
  cursor: "pointer",
  transition: "border 0.3s ease-in-out",
  padding: "2px",
  "&:hover": {
    border: "1px solid #1875CC",
  },
}));

const ButtonContainer = styled("div")(({ theme }) => ({
  padding: "6px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#fff",
  transition: "background 0.3s ease-in-out, opacity 0.3s ease-in-out",
  cursor: "pointer",
  filter: "drop-shadow(rgba(0, 0, 0, 0.1) 0px 2px 10px)",
  gap: "10px",
  position: "absolute",
  zIndex: 2,
  height: "32px",
  width: "32px",
  top: "100px",
  "&:hover": {
    background: "#f2f2f2",
  },
  "& svg": {
    width: "16px",
    height: "16px",
  },
}));

const AccessoryItemPage = ({ isMobileVersion }) => {
  const navigate = useNavigate();
  const isBelow1200 = useMediaQuery("(max-width:1000px)");
  const { productId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(1);
  const [readyToBy, setReadyToBy] = useState({
    type: "",
    open: false,
  });

  useEffect(() => {
    GetAccessoryById({ id: productId })
      .then((resp) => {
        if (resp.data) {
          setData(resp.data);
        }
      })
      .catch(() => {
        navigate("/");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, productId]);

  const getFirstImage = useCallback(() => {
    if (data.imageNames && data.imageNames.length > 0) {
      return `${getBaseUrl()}/images/${data.imageNames[0]}`;
    }
    return null;
  }, [data]);

  const getSecondImage = useCallback(() => {
    if (data.imageNames && data.imageNames.length > 1) {
      return `${getBaseUrl()}/images/${data.imageNames[1]}`;
    }
    return null;
  }, [data]);

  const getThirdImage = useCallback(() => {
    if (data.imageNames && data.imageNames.length > 2) {
      return `${getBaseUrl()}/images/${data.imageNames[2]}`;
    }
    return null;
  }, [data]);

  const onCopyProductId = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        enqueueSnackbar(t("linkCopied"), {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const goLeft = () => {
    if (selectedImage === 1) {
      setSelectedImage(3);
    } else {
      setSelectedImage(selectedImage - 1);
    }
  };

  const goRight = () => {
    if (selectedImage === 3) {
      setSelectedImage(1);
    } else {
      setSelectedImage(selectedImage + 1);
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://webstatic.bog.ge/bog-sdk/bog-sdk.js?version=2&client_id=${process.env.REACT_APP_BOG_INSTALLMENT_CLIENT_ID}`;
    script.async = true;
    script.id = "bog-sdk-script";

    document.body.appendChild(script);

    return () => {
      const existing = document.getElementById("bog-sdk-script");
      if (existing) {
        document.body.removeChild(existing);
      }

      // დამატებით სუფთავდება window.BOG თუ გინდა:
      delete window.BOG;
    };
  }, []);

  const AccessoryTypeEnum = [
    "ScreenProtector",
    "MobilePhoneCase",
    "WirelessCharger",
    "PowerBank",
    "Cable",
    "ChargingAdapter",
    "CarAccessory",
    "LaptopAccessory",
    "TabletAccessory",
  ];

  const OnlineBuyContent = () => {
    return (
      <Content>
        <ByItemContainer>
          <ByItemContainerTitle>
            <Price>{data && `${data.price} ₾`}</Price>
          </ByItemContainerTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px",
              gap: "10px",
              flexDirection: "column",
            }}
          >
            <Tooltip
              title={
                data.hasBuyOnline
                  ? ""
                  : data?.outOfStock
                    ? t("outOfStock")
                    : t("temporarilyUnavailable")
              }
            >
              <div
                style={{
                  opacity: data?.hasBuyOnline ? 1 : 0.5,
                  width: "100%",
                }}
              >
                <BuyButton
                  onClick={() => {
                    if (data.hasBuyOnline && !data?.outOfStock) {
                      setReadyToBy({
                        open: true,
                        type: "BOGBUY",
                      });
                    }
                  }}
                >
                  <img
                    src={"/assets/bank/bog.png"}
                    width={24}
                    alt={"online buy bog"}
                  />{" "}
                  {t("buyBog")}
                </BuyButton>
              </div>
            </Tooltip>

            <Tooltip
              title={
                data?.hasBogInstallment
                  ? ""
                  : data?.outOfStock
                    ? t("outOfStock")
                    : t("temporarilyUnavailable")
              }
            >
              <div
                style={{
                  opacity: data?.hasBogInstallment ? 1 : 0.5,
                  width: "100%",
                }}
              >
                <BuyButton
                  onClick={() => {
                    if (data.hasBogInstallment && !data?.outOfStock) {
                      setReadyToBy({
                        open: true,
                        type: "BOGINSTALLMENT",
                      });
                    }
                  }}
                >
                  <img
                    src={"/assets/bank/bog.png"}
                    width={24}
                    alt={"installment bog"}
                  />{" "}
                  {t("bogInstallment")}
                </BuyButton>
              </div>
            </Tooltip>

            <Tooltip
              title={
                data?.hasTbcInstallment
                  ? ""
                  : data?.outOfStock
                    ? t("outOfStock")
                    : t("temporarilyUnavailable")
              }
            >
              <div
                style={{
                  opacity: data?.hasTbcInstallment ? 1 : 0.5,
                  width: "100%",
                }}
              >
                <BuyButton
                  onClick={() =>
                    data.hasTbcInstallment &&
                    !data?.outOfStock &&
                    setReadyToBy({
                      open: true,
                      type: "TBCINSTALLMENT",
                    })
                  }
                >
                  <img
                    src={"/assets/bank/tbc.png"}
                    width={24}
                    alt={"installment tbc"}
                  />{" "}
                  {t("tbcInstallment")}
                </BuyButton>
              </div>
            </Tooltip>

            <Tooltip
              title={
                data?.hasCredoInstallment
                  ? ""
                  : data?.outOfStock
                    ? t("outOfStock")
                    : t("temporarilyUnavailable")
              }
            >
              <div
                style={{
                  opacity: data?.hasCredoInstallment ? 1 : 0.5,
                  width: "100%",
                }}
              >
                <BuyButton
                  onClick={() =>
                    data.hasCredoInstallment &&
                    !data?.outOfStock &&
                    setReadyToBy({
                      open: true,
                      type: "CREDOINSTALLMENT",
                    })
                  }
                >
                  <img
                    src={"/assets/bank/kredo.png"}
                    width={24}
                    alt={"installment credo"}
                  />
                  {t("credoInstallment")}
                </BuyButton>
              </div>
            </Tooltip>
          </div>
        </ByItemContainer>
      </Content>
    );
  };

  return (
    <Root>
      <Content>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <NavigateContainer>
            {isMobile ? (
              <>
                <NavigateBefore
                  onClick={() => navigate("/accessories")}
                  sx={{ marginLeft: "-8px" }}
                />
                <NavigateItem>
                  {
                    BrandEnumArray.find(
                      (item) => item.value === data.mobileBrandEnum,
                    )?.Name
                  }
                </NavigateItem>
              </>
            ) : (
              <>
                <NavigateItem onClick={() => navigate("/")}>
                  {t("main")}
                </NavigateItem>
                <NavigateNextIcon />
                <NavigateItem onClick={() => navigate("/accessories")}>
                  {t("accessories")}
                </NavigateItem>
                <NavigateNextIcon />
                <NavigateItem>
                  {
                    BrandEnumArray.find(
                      (item) => item.value === data.mobileBrandEnum,
                    )?.Name
                  }
                </NavigateItem>
              </>
            )}
          </NavigateContainer>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: "10px",
            }}
            sx={{
              color: "#1875CC",
            }}
            onClick={() => onCopyProductId()}
          >
            <ShareIcon
              style={{
                width: "20px",
                height: "20px",
              }}
            />
            <span>MobileStore-{productId}</span>
          </Box>
        </div>

        <Divider
          style={{
            margin: isMobileVersion ? "10px 0px" : "20px 0px",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "20px",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {loading ? (
            <Skeleton
              variant="rounded"
              style={{
                minWidth: "380px",
              }}
              width={"100%"}
              height={"380px"}
            />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                width: isMobile ? "auto" : "400px",
              }}
            >
              <div
                style={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "auto",
                  fontWeight: "600",
                  fontSize: "16px",
                  maxWidth: "400px",
                }}
              >
                {data.name}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                {getFirstImage() && (
                  <img
                    width={300}
                    height={300}
                    src={
                      selectedImage === 1
                        ? getFirstImage()
                        : selectedImage === 2
                          ? getSecondImage()
                          : getThirdImage()
                    }
                    alt={"image"}
                  />
                )}

                <ButtonContainer
                  onClick={() => goLeft()}
                  style={{
                    left: "30px",
                  }}
                  id={"banner_left_arrow"}
                >
                  <ArrowBackIosNewIcon
                    style={{
                      color: "#1875CC",
                    }}
                  />
                </ButtonContainer>

                <ButtonContainer
                  onClick={() => goRight()}
                  style={{
                    right: "30px",
                  }}
                  id={"banner_left_arrow"}
                >
                  <ArrowForwardIosIcon
                    style={{
                      color: "#1875CC",
                    }}
                  />
                </ButtonContainer>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "30px",
                  justifyContent: "center",
                }}
              >
                <div>
                  {getFirstImage() && (
                    <StyledImageContainer
                      isSelected={selectedImage === 1}
                      onClick={() => setSelectedImage(1)}
                    >
                      <img
                        width={50}
                        height={50}
                        src={getFirstImage()}
                        alt={"image"}
                      />
                    </StyledImageContainer>
                  )}
                </div>
                <div>
                  {getSecondImage() && (
                    <StyledImageContainer
                      isSelected={selectedImage === 2}
                      onClick={() => setSelectedImage(2)}
                    >
                      <img
                        width={50}
                        height={50}
                        src={getSecondImage()}
                        alt={"image"}
                      />
                    </StyledImageContainer>
                  )}
                </div>
                <div>
                  {getThirdImage() && (
                    <StyledImageContainer
                      isSelected={selectedImage === 3}
                      onClick={() => setSelectedImage(3)}
                    >
                      <img
                        width={50}
                        height={50}
                        src={getThirdImage()}
                        alt={"image"}
                      />
                    </StyledImageContainer>
                  )}
                </div>
              </div>
            </div>
          )}
          {loading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: "20px",
              }}
            >
              <Skeleton
                variant="rounded"
                style={{
                  minWidth: "250px",
                }}
                width={"100%"}
                height={"20px"}
              />
              <Skeleton
                variant="rounded"
                style={{
                  minWidth: "230px",
                }}
                width={"100%"}
                height={"20px"}
              />
              <Skeleton
                variant="rounded"
                style={{
                  minWidth: "230px",
                }}
                width={"100%"}
                height={"20px"}
              />
              <Skeleton
                variant="rounded"
                style={{
                  minWidth: "230px",
                }}
                width={"100%"}
                height={"20px"}
              />
              <Skeleton
                variant="rounded"
                style={{
                  minWidth: "230px",
                }}
                width={"100%"}
                height={"20px"}
              />
              <Skeleton
                variant="rounded"
                style={{
                  minWidth: "230px",
                }}
                width={"100%"}
                height={"20px"}
              />
              <Skeleton
                variant="rounded"
                style={{
                  minWidth: "230px",
                }}
                width={"100%"}
                height={"20px"}
              />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: isMobile ? "100%" : "250px",
                gap: "20px",
              }}
            >
              <ProductMainInfoItem>
                <ProductMainInfoItemKey>
                  {t("accessoryType")}
                </ProductMainInfoItemKey>
                <ProductMainInfoItemValue>
                  {t(
                    `AccessoryTypeEnum${AccessoryTypeEnum[data.accessoryType]}`,
                  )}
                </ProductMainInfoItemValue>
              </ProductMainInfoItem>
              <ProductMainInfoItem>
                <ProductMainInfoItemKey>
                  {t("description")}
                </ProductMainInfoItemKey>
                <ProductMainInfoItemValue>
                  {data?.description}
                </ProductMainInfoItemValue>
              </ProductMainInfoItem>
            </div>
          )}
        </div>

        {!isMobile && isBelow1200 && (
          <div
            style={{
              marginTop: "20px",
            }}
          >
            <OnlineBuyContent />
          </div>
        )}

        <Divider
          style={{
            margin: "20px 0px",
          }}
        />
      </Content>

      {isMobile ? (
        <ProductItemPageFooter
          data={data}
          setReadyToBy={setReadyToBy}
          onClickAdd={() => {}}
        />
      ) : !isBelow1200 ? (
        <OnlineBuyContent />
      ) : null}
      {readyToBy.open && (
        <MobileReadyToBuy
          readyToBy={readyToBy}
          setReadyToBy={setReadyToBy}
          productId={productId}
          data={data}
        />
      )}
    </Root>
  );
};

export default AccessoryItemPage;
